import axios from 'axios';
import { useState } from 'react';
import jwtDecode from 'jwt-decode';
import {
  useStoreValue,
  OKTA_LOGIN_AND_SEND_OTP_ERROR,
  OKTA_SYNTHETIC_TOKEN_ERROR,
  OKTA_VERIFICATION_LOCKED_ERROR,
  OKTA_VERIFICATION_CODE_INCORRECT_ERROR,
  OKTA_VERIFICATION_ERROR,
  OKTA_ERROR_CODE_INVALID_PASSCODE_FOR_INCORRECT_OTP,
  OKTA_ERROR_CODE_INVALID_TOKEN_FOR_EXPIRED_OTP,
  OKTA_ERROR_CODE_SMS_WAIT_30_SECS,
  OKTA_ERROR_CODE_CAN_ONLY_BE_USED_ONCE,
  OKTA_OTP_TOO_OFTEN_ERROR,
  OKTA_EMPTY_MOBILE_NUMBER_ERROR,
  OKTA_SMS_FACTOR,
  ERROR_KIND_USER_ERROR,
  ERROR_KIND_SYSTEM_ERROR,
} from 'store';
import { oktaAuthClient } from 'library/oktaAuthClient';
import { RESPONSE_TYPE, SCOPES } from '_config/_constants/verificationPage';
import { getLatitudeId, getAuthHeader, byCountry } from 'utils';
import { datadogRum } from '@datadog/browser-rum';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useEnv } from './useEnv';
import { useHandleApiErrors } from './useHandleApiErrors';
import { useGetUserInfo } from './useGetUserInfo';

/**
 *  find OTP factor by factor type
 * @param {object} item
 * @param {string} factorType
 * @returns {object} factor
 */
const findOTPFactor = (item, factorType) => {
  return item.factors.find(factor => factor.provider === 'OKTA' && factor.factorType === factorType);
};

const oktaSmsFactor = OKTA_SMS_FACTOR || 'sms';
const oktaLoginAndSendOtpError = OKTA_LOGIN_AND_SEND_OTP_ERROR || 'OKTA_LOGIN_AND_SEND_OTP_ERROR';
const oktaOtpTooOftenError = OKTA_OTP_TOO_OFTEN_ERROR || 'OKTA_OTP_TOO_OFTEN_ERROR';
const oktaSyntheticTokenError = OKTA_SYNTHETIC_TOKEN_ERROR || 'OKTA_SYNTHETIC_TOKEN_ERROR';
const oktaVerificationError = OKTA_VERIFICATION_ERROR || 'OKTA_VERIFICATION_ERROR';
const oktaVerificationLockedError = OKTA_VERIFICATION_LOCKED_ERROR || 'OKTA_VERIFICATION_LOCKED_ERROR';
const oktaVerificationCodeIncorrectError =
  OKTA_VERIFICATION_CODE_INCORRECT_ERROR || 'OKTA_VERIFICATION_CODE_INCORRECT_ERROR';
const oktaEmptyMobileNumberError = OKTA_EMPTY_MOBILE_NUMBER_ERROR || 'OKTA_EMPTY_MOBILE_NUMBER_ERROR';
const oktaInvalidPasscodeErrorCode = OKTA_ERROR_CODE_INVALID_PASSCODE_FOR_INCORRECT_OTP || 'E0000068';
const oktaExpiredTokenErrorCode = OKTA_ERROR_CODE_INVALID_TOKEN_FOR_EXPIRED_OTP || 'E0000011';
const oktaSmsWaitErrorCode = OKTA_ERROR_CODE_SMS_WAIT_30_SECS || 'E0000109';
const oktaCanOnlyBeUsedOnceErrorCode = OKTA_ERROR_CODE_CAN_ONLY_BE_USED_ONCE || 'E0000082';
const userErrorKind = ERROR_KIND_USER_ERROR || 'user-error';
const systemErrorKind = ERROR_KIND_SYSTEM_ERROR || 'system-error';

/**
 *  useOktaClient hook
 * @typedef {Object} params
 * @property {function} onSuccess
 * @property {function} onError
 *
 * @returns {Object} object
 */

const useOktaClient = ({ onSuccess, onError } = {}) => {
  const getEnv = useEnv();
  const [store = {}, updateStore = () => {}] = useStoreValue() || [];
  const { acquisition = {}, oktaAuthTransaction = {} } = store;
  const [loading, setLoading] = useState(false);
  const { handleError } = useHandleApiErrors('okta', onError);
  const { getUserInfo } = useGetUserInfo({ onError });
  const launchDarklyClient = useLDClient();

  const sendOTP = async (txn, factorType) => {
    const targetFactor = findOTPFactor(txn, factorType);
    await targetFactor.verify();
    updateStore({
      applicationErrors: null,
      oktaAuthTransaction: txn,
    });
  };

  /**
   *  login and send OTP
   * @param {string} email
   * @param {string} factor
   * @param {boolean} clearToken
   * @returns {boolean} success
   */
  const loginAndSendOTP = async (email, factor = oktaSmsFactor, clearToken = true) => {
    try {
      setLoading(true);

      updateStore({
        oktaAuthTransaction: null,
        oktaFactor: factor,
      });

      if (clearToken) {
        // in case browser had a cached token, clear it
        oktaAuthClient.tokenManager.clear();
        oktaAuthClient.closeSession();
      }

      const authData = await oktaAuthClient.signInWithCredentials({ username: email });
      await sendOTP(authData, factor);
      setLoading(false);
      onSuccess && onSuccess({});
      return true;
    } catch (error) {
      if (error.errorCode && error.errorCode === oktaSmsWaitErrorCode) {
        setLoading(false);
        handleError({ type: oktaOtpTooOftenError, error, kind: userErrorKind }, false);
        return true;
      }
      setLoading(false);
      handleError({ type: oktaLoginAndSendOtpError, error, kind: systemErrorKind });
      return false;
    }
  };

  /**
   *  logout current user
   * @description calls oktaAuthClient.signOut, clears sessionStorage and localStorage.
   * Please Note: This will redirect to an Okta-hosted page before returning to your app.
   */

  const logout = async () => {
    await oktaAuthClient.signOut({
      revokeAccessToken: true,
      revokeRefreshToken: true,
    });
  };

  /**
   *  logout(soft) current user without redirecting to Okta login page
   * @description calls oktaAuthClient.revokeAccessToken & oktaAuthClient.revokeRefreshToken, clears sessionStorage and localStorage.
   */

  const softLogout = async () => {
    await oktaAuthClient.revokeAccessToken();
    await oktaAuthClient.revokeRefreshToken();
    sessionStorage.clear();
    localStorage.clear();
  };

  /**
   *  resend OTP
   * @param {string} factor
   * @returns {boolean} success
   */
  const resendOTP = (factor = 'sms') => {
    return loginAndSendOTP(acquisition.contactDetails.emailAddress, factor);
  };

  /**
   *  apply synthetic token
   * @returns {boolean} success
   */
  const applySyntheticToken = async () => {
    const token = getEnv('VITE_OKTA_TOKEN');
    if (!token && (import.meta.env.VITE_ENV === 'dev' || import.meta.env.VITE_ENV === 'staging')) {
      // eslint-disable-next-line
      console.warn('There is no synthetic token present');
      return true;
    }
    try {
      const decoded = jwtDecode(token);
      oktaAuthClient.tokenManager.setTokens({
        accessToken: {
          accessToken: token,
          claims: decoded,
        },
      });
      updateStore({
        applicationErrors: null,
        latitudeId: getLatitudeId(),
      });
      return true;
    } catch (error) {
      handleError({ type: oktaSyntheticTokenError, error, kind: systemErrorKind });
      return false;
    }
  };

  /**
   *  get user statuses
   * @returns {object} user statuses
   */
  const getUserStatuses = async () => {
    const method = 'GET';
    const url = `${import.meta.env.VITE_USER_REGISTER_URL}/statuses`;
    const headers = {
      Authorization: getAuthHeader(),
      'lfs-request-channel': byCountry({
        NZ: import.meta.env.VITE_OKTA_HEADER,
        AU: import.meta.env.VITE_OKTA_HEADER_AU,
      }),
      'Content-Type': 'application/json',
    };
    const response = await axios.request({
      method,
      url,
      headers,
    });

    const result = {
      isLatitudeIdPasswordSet: response?.data?.status_initial_password_set,
      isEmailVerified: response?.data?.status_email_verified,
    };
    return result;
  };

  const getAndSetAccessToken = async txn => {
    const result = await oktaAuthClient.token.getWithoutPrompt({
      sessionToken: txn.sessionToken,
      responseType: RESPONSE_TYPE,
      scopes: SCOPES,
    });

    const { tokens } = result;
    oktaAuthClient.tokenManager.setTokens(tokens);
    return tokens;
  };

  /**
   *  verify OTP
   * @param {string} passCode
   * @param {object} options
   * @param {string} factor
   * @returns {object} result
   */
  const verifyOTP = async (passCode, { checkIsLatitudeIdPasswordSet } = {}, factor = oktaSmsFactor) => {
    try {
      setLoading(true);
      let targetFactor = findOTPFactor(oktaAuthTransaction, factor);
      // after page refreshing, verify function will lost from session storage
      if (!targetFactor.verify) {
        const authData = await oktaAuthClient.signInWithCredentials({
          username: acquisition.contactDetails.emailAddress,
        });
        targetFactor = findOTPFactor(authData, factor);
      }
      const verifyResult = await targetFactor.verify({
        passCode,
      });
      const tokens = await getAndSetAccessToken(verifyResult);

      const decoded = jwtDecode(tokens.accessToken.accessToken);
      const userInfo = await getUserInfo(tokens.accessToken.accessToken);

      const { mobile_phone: mobileNumber } = userInfo;

      updateStore({
        applicationErrors: null,
        latitudeId: decoded.sub,
      });

      if ([undefined, ''].includes(mobileNumber)) {
        const error = new Error(
          `Okta profile mobile number is blank. Latitude ID: ${decoded.sub}. Application ID: ${store.applicationId}.`,
        );
        error.kind = systemErrorKind;
        error.fingerprint = `OKTA_EMPTY_MOBILE_NUMBER::latitude-id::${decoded.sub}`;
        error.type = oktaEmptyMobileNumberError;
        datadogRum.addError(error);

        updateStore({
          oktaProfileHealthy: false,
        });
      } else {
        updateStore({
          acquisition: {
            ...acquisition,
            contactDetails: {
              ...acquisition.contactDetails,
              mobileNumber,
            },
          },
        });
      }
      datadogRum.setUser({ id: decoded.sub });

      if (launchDarklyClient) {
        // set LaunchDarkly user key in its context so that user will receive consistent flags across resumes.
        const { anonymous, ...existingLdContext } = launchDarklyClient.getContext();
        const isUsingTargetRole = !anonymous;

        if (!isUsingTargetRole) {
          const latitudeId = getLatitudeId();
          await launchDarklyClient.identify({
            ...existingLdContext,
            kind: 'user',
            key: latitudeId,
            anonymous: false,
          });
        }
      }

      if (checkIsLatitudeIdPasswordSet) {
        const { isLatitudeIdPasswordSet, isEmailVerified } = await getUserStatuses();
        setLoading(false);
        updateStore({
          isLatitudeIdPasswordSet,
          isEmailVerified,
        });
        onSuccess && (await onSuccess({ isLatitudeIdPasswordSet, isEmailVerified }));
      } else {
        setLoading(false);
        onSuccess && (await onSuccess({}));
      }
      return {};
    } catch (error) {
      setLoading(false);

      if (
        error.errorCode === oktaInvalidPasscodeErrorCode ||
        error.errorCode === oktaExpiredTokenErrorCode ||
        error.errorCode === oktaCanOnlyBeUsedOnceErrorCode
      ) {
        handleError({ type: oktaVerificationCodeIncorrectError, error, kind: userErrorKind }, false);
        return {
          errorMessage:
            'The verification code is incorrect. Please check the code, or resend a new code and try again.',
        };
      }
      if (error.errorCode === 'E0000069') {
        handleError({ type: oktaVerificationLockedError, error, kind: userErrorKind });
        return {};
      }

      handleError({ type: oktaVerificationError, error, kind: userErrorKind });
      return {};
    }
  };

  return {
    loading,
    loginAndSendOTP,
    verifyOTP,
    resendOTP,
    applySyntheticToken,
    logout,
    softLogout,
  };
};

export { useOktaClient };
